import { useState, useCallback, useEffect } from "react";

interface UseKeyboardNavigationOptions {
	itemCount: number;
	isOpen: boolean;
	onSelect: (index: number) => void;
	onClose: () => void;
	skipIndices?: number[];
	loop?: boolean;
}

interface UseKeyboardNavigationReturn {
	highlightedIndex: number;
	setHighlightedIndex: (index: number) => void;
	getKeyboardProps: () => {
		onKeyDown: (e: React.KeyboardEvent) => void;
	};
}

export function useKeyboardNavigation({
	itemCount,
	isOpen,
	onSelect,
	onClose,
	skipIndices = [],
	loop = true,
}: UseKeyboardNavigationOptions): UseKeyboardNavigationReturn {
	const [highlightedIndex, setHighlightedIndex] = useState(-1);

	// Reset when closed
	useEffect(() => {
		if (!isOpen) {
			setHighlightedIndex(-1);
		}
	}, [isOpen]);

	const getNextIndex = useCallback(
		(current: number, direction: 1 | -1): number => {
			if (itemCount === 0) return -1;

			let next = current;
			const maxIterations = itemCount; // prevent infinite loop

			for (let i = 0; i < maxIterations; i++) {
				next += direction;

				if (loop) {
					if (next >= itemCount) next = 0;
					if (next < 0) next = itemCount - 1;
				} else {
					if (next >= itemCount || next < 0) return current;
				}

				if (!skipIndices.includes(next)) return next;
			}

			return current;
		},
		[itemCount, loop, skipIndices],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (!isOpen) return;

			switch (e.key) {
				case "ArrowDown":
					e.preventDefault();
					setHighlightedIndex((prev) => getNextIndex(prev, 1));
					break;
				case "ArrowUp":
					e.preventDefault();
					setHighlightedIndex((prev) => getNextIndex(prev, -1));
					break;
				case "Home":
					e.preventDefault();
					setHighlightedIndex(0);
					break;
				case "End":
					e.preventDefault();
					setHighlightedIndex(itemCount - 1);
					break;
				case "Enter":
					e.preventDefault();
					if (highlightedIndex >= 0) {
						onSelect(highlightedIndex);
					}
					break;
				case "Escape":
					e.preventDefault();
					onClose();
					break;
			}
		},
		[isOpen, highlightedIndex, itemCount, onSelect, onClose, getNextIndex],
	);

	const getKeyboardProps = useCallback(
		() => ({ onKeyDown: handleKeyDown }),
		[handleKeyDown],
	);

	return {
		highlightedIndex,
		setHighlightedIndex,
		getKeyboardProps,
	};
}
